/** Profile: identity header, stat tiles, settings, and the links out to the rest of the app. */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { MeProfile } from '@promptvault/shared';
import { patchSettings } from '../api/me';
import { Padded, Screen } from '../components/Screen';
import { useToast } from '../components/Toast';
import { ErrorState, SkeletonCells } from '../components/feedback';
import {
  Avatar,
  Cell,
  CellBadge,
  SegmentedControl,
  Section,
  Toggle,
} from '../components/primitives';
import { compactNumber } from '../lib/format';
import { useI18n, type Lang } from '../lib/i18n';
import { hapticNotify } from '../hooks/useTg';
import { openTelegramLink, telegramUser } from '../lib/telegram';
import { useMe } from '../providers/AppProviders';

const BOT_USERNAME = 'PromptVaultAIBot';
const ONBOARDING_KEY = 'pv.onboarded';

export function Profile() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { t, lang, setLang } = useI18n();
  const me = useMe();
  const tgUser = telegramUser();

  const settingsMut = useMutation({
    mutationFn: patchSettings,
    onMutate: async (patch) => {
      // Toggles must flip instantly; the server response reconciles afterwards.
      const previous = qc.getQueryData<MeProfile>(['me']);
      if (previous) {
        qc.setQueryData<MeProfile>(['me'], {
          ...previous,
          settings: { ...previous.settings, ...patch },
        });
      }
      return { previous };
    },
    onSuccess: (updated) => {
      hapticNotify('success');
      qc.setQueryData(['me'], updated);
      void qc.invalidateQueries({ queryKey: ['feed'] });
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(['me'], ctx.previous);
      hapticNotify('error');
      toast(t.errorTitle, 'error');
    },
  });

  if (!me) {
    return (
      <Screen root>
        <Padded className="pt-3.5">
          <SkeletonCells count={4} />
        </Padded>
      </Screen>
    );
  }

  const stats = [
    { value: me.downloadCount, label: t.downloads },
    { value: me.ratingsGiven, label: t.ratingsGiven },
    { value: me.submissions, label: t.submissions },
  ];

  return (
    <Screen root chrome="page">
      <Padded className="pt-3.5">
        <div className="flex items-center gap-3 rounded-card bg-surface p-3.5">
          <Avatar name={me.name} photoUrl={me.photoUrl ?? tgUser?.photo_url ?? null} size={58} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-title font-semibold text-text">{me.name}</p>
            {me.username && (
              <p className="bidi-plaintext truncate text-subhead text-hint">@{me.username}</p>
            )}
            {me.isPremium && (
              <p className="pt-0.5 text-footnote font-medium text-accent">{t.proActive}</p>
            )}
          </div>
        </div>
      </Padded>

      <Padded className="flex gap-2 pt-2">
        {stats.map((s) => (
          <div
            key={s.label}
            className="flex min-w-0 flex-1 flex-col items-start gap-0.5 rounded-card bg-surface px-2.5 py-3"
          >
            <span className="tnum text-title font-bold text-text">
              {compactNumber(s.value, lang)}
            </span>
            <span className="text-caption leading-tight text-hint">{s.label}</span>
          </div>
        ))}
      </Padded>

      <Padded className="pt-5">
        <Section header={t.settings}>
          <Cell
            icon="globe"
            title={t.language}
            after={
              <SegmentedControl<Lang>
                size="sm"
                value={lang}
                onChange={(next) => {
                  setLang(next);
                  settingsMut.mutate({ language: next });
                }}
                options={[
                  { value: 'en', label: 'EN' },
                  { value: 'fa', label: 'فارسی' },
                ]}
              />
            }
          />
          <Cell
            icon="eye"
            title={t.nsfwLabel}
            subtitle={t.nsfwSub}
            after={
              <Toggle
                label={t.nsfwLabel}
                checked={me.settings.nsfwVisible}
                onChange={(next) => settingsMut.mutate({ nsfwVisible: next })}
              />
            }
          />
          <Cell
            icon="bell"
            title={t.notifLabel}
            subtitle={t.notifSub}
            last
            after={
              <Toggle
                label={t.notifLabel}
                checked={me.settings.notifications}
                onChange={(next) => settingsMut.mutate({ notifications: next })}
              />
            }
          />
        </Section>
      </Padded>

      <Padded className="pt-5">
        <Section>
          <Cell icon="upload" title={t.submitPrompt} chevron onClick={() => navigate('/submit')} />
          <Cell
            icon="trophy"
            title={t.leaderboard}
            chevron
            onClick={() => navigate('/leaderboard')}
          />
          <Cell icon="chat" title={t.supportChat} chevron onClick={() => navigate('/support')} />
          {!me.isPremium && (
            <Cell
              icon="sparkle"
              title={t.goPro}
              badge={<CellBadge>★</CellBadge>}
              chevron
              onClick={() => openTelegramLink(`https://t.me/${BOT_USERNAME}?start=pro`)}
            />
          )}
          <Cell
            icon="chat"
            title={t.openBot}
            chevron
            onClick={() => openTelegramLink(`https://t.me/${BOT_USERNAME}`)}
          />
          <Cell
            icon="sparkle"
            title={t.showIntro}
            chevron
            last
            onClick={() => {
              try {
                localStorage.removeItem(ONBOARDING_KEY);
              } catch {
                /* private mode */
              }
              navigate('/welcome');
            }}
          />
        </Section>
      </Padded>

      <p className="px-gutter py-5 text-center text-caption text-subtitle">{t.version}</p>
    </Screen>
  );
}

/** Rendered when /me itself fails — the profile has no partial state worth showing. */
export function ProfileError({ onRetry, message }: { onRetry: () => void; message?: string }) {
  return (
    <Screen root>
      <ErrorState onRetry={onRetry} message={message} />
    </Screen>
  );
}
